<script setup lang="ts">
import { computed, ref } from 'vue'
import { useSetTimeout } from '@lit-element/components/captcha/src/hooks/useSetTimeout'
import { useNamespace } from '@lit-element/hooks'
import { throttle } from '@lit-element/components/resize-observer/src/performance'
import Slider from '@lit-element/components/slider'
import ResizeObserver from '../../resize-box/src/resize-observer'
import { useListener } from './hooks/listener'
import { boundRange, formatSeconds, getStepByWord } from './hooks/utils'
import { localeVideo } from './symbol'
import type { PropType } from 'vue'
import type { VideoSegment } from './symbol'

interface PointState {
  start: number
  startPercent: number
  end: number
  endPercent: number
  duration: number
  durationPercent: number
  width: number
}

defineOptions({ name: 'VideoProgress' })

const props = defineProps({
  time: {
    type: Number,
    default: 0,
  },
  duration: {
    type: Number,
    default: 0,
  },
  segments: {
    type: Array as PropType<VideoSegment[]>,
    default: () => [],
  },
  noPreview: {
    type: Boolean,
    default: false,
  },
  previewSrc: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['change'])

const ns = useNamespace('video')

const { timer } = useSetTimeout()

const slidTime = ref(0)
const hovered = ref(false)
const hoveredTime = ref(0)
const indicatorLeft = ref(0)
const previewLeft = ref(0)

let paddingX = [0, 0]
let sliderWidth = 100
let previewWidth = 60

const wrapper = ref<HTMLElement>()
const slider = ref()
const sliderEl = computed(() => slider.value?.$el as HTMLElement | undefined)
const preview = ref<HTMLElement>()

const sliderVal = ref(0)
const sliding = computed(() => {
  return !!slider.value?.firstButton?.dragging
})
// const percent = computed(() => {
//   return props.duration
//     ? ((sliding.value ? slidTime.value : props.time) / props.duration) * 100
//     : 0
// })
const className = computed(() => {
  return {
    [ns.e('progress')]: true,
    [ns.em('progress', 'sliding')]: sliding.value,
    [ns.em('progress', 'disabled')]: props.duration <= 0,
  }
})
const points = computed<PointState[]>(() => {
  const duration = Math.max(1, props.duration)

  let times = props.segments.map((segment) => segment.time)

  if (!times.length) times = [0, duration]

  times = times.at(-1) === duration ? times : [...times, duration]
  times = times[0] === 0 ? times : [0, ...times]

  const points: PointState[] = []

  for (let i = 0, len = times.length - 1; i < len; ++i) {
    const start = times[i]
    const end = times[i + 1]
    const pointDuration = end - start

    points.push({
      start,
      startPercent: (start / duration) * 100,
      end,
      endPercent: (end / duration) * 100,
      duration: pointDuration,
      durationPercent: (pointDuration / duration) * 100,
      width: ((end - start) / duration) * 100,
    })
  }

  return points
})
const segmentLabel = computed(() => {
  const time = hoveredTime.value

  const segments = props.segments

  if (!segments.length) return ''

  let index = -1

  if (time <= 0) {
    index = 0
  } else {
    for (let i = 1, len = segments.length; i < len; ++i) {
      if (segments[i].time > time) {
        index = i - 1
        break
      }
    }
  }

  if (index < 0) {
    index = segments.length - 1
  }

  const title =
    segments[index]?.title || getStepByWord(localeVideo.chapterCount, index + 1)

  return title && ` (${title})`
})

useListener(sliderEl, 'pointerenter', () => {
  clearTimeout(timer.hover)

  timer.hover = setTimeout(() => {
    hovered.value = true
  }, 100)
})
useListener(sliderEl, 'pointerleave', () => {
  clearTimeout(timer.hover)

  timer.hover = setTimeout(() => {
    hovered.value = false
  }, 100)
})
useListener(
  sliderEl,
  'pointermove',
  throttle((event: PointerEvent) => {
    if (!sliding.value) {
      processMoveOnTrack(event)
    }
  })
)

function processMoveOnTrack(event: PointerEvent) {
  if (!sliderEl.value) return

  const offsetX = boundRange(
    event.clientX - sliderEl.value.getBoundingClientRect().left,
    0,
    sliderWidth
  )

  hoveredTime.value = (offsetX / sliderWidth) * props.duration
  indicatorLeft.value = offsetX + paddingX[0]
  previewLeft.value = boundRange(
    offsetX - previewWidth * 0.5 + paddingX[0],
    0,
    sliderWidth - previewWidth + paddingX[0] + paddingX[1]
  )
}

function onSliderResize(entry: ResizeObserverEntry) {
  console.log('onSliderResize')
  if (!wrapper.value) return

  const style = getComputedStyle(wrapper.value)

  paddingX = [
    Number.parseFloat(style.paddingLeft),
    Number.parseFloat(style.paddingRight),
  ]
  sliderWidth = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
}

function onPreviewResize(entry: ResizeObserverEntry) {
  previewWidth = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
}

function handleChange(permillage: any) {
  emit('change', (permillage / 1000) * props.duration)
}

const onSlideMove = throttle(processMoveOnTrack)

function onSlideStart() {
  slidTime.value = props.time
  document.addEventListener('pointermove', onSlideMove)
  document.addEventListener('pointerup', onSlideEnd)
}

function onSlideEnd() {
  document.removeEventListener('pointermove', onSlideMove)
  document.removeEventListener('pointerup', onSlideEnd)
}
</script>
<template>
  <!-- :model-value="percent * 10" -->
  <div ref="wrapper" :class="className">
    <ResizeObserver @resize="onSliderResize">
      <Slider
        ref="slider"
        v-model="sliderVal"
        style="height: 14px"
        :class="ns.e('progress-slider')"
        :max="1000"
        :vertical="false"
        :range="false"
        :show-tooltip="false"
        :disabled="duration <= 0"
        @change="handleChange"
        @pointerdown="onSlideStart"
      >
        <template #filler="state">
          <div
            v-for="(point, index) in points"
            :key="index"
            :class="ns.e('progress-segment')"
            :style="{ width: `${point.width}%` }"
          >
            <div :class="ns.e('progress-track')">
              <div
                :class="ns.e('progress-filler')"
                :style="{
                  visibility:
                    state.percent[1] < point.startPercent
                      ? 'hidden'
                      : undefined,
                  transform: `translateX(${Math.min(
                    (Math.max(state.percent[1] - point.startPercent, 0) /
                      point.durationPercent) *
                      100 -
                      100,
                    0
                  )}%) translateZ(0)`,
                }"
              />
            </div>
          </div>
        </template>
        <template #trigger>
          <slot name="trigger">
            <div :class="ns.e('progress-trigger')" />
          </slot>
        </template>
      </Slider>
    </ResizeObserver>
    <div
      :class="{
        [ns.e('progress-indicator')]: true,
        [ns.em('progress-indicator', 'active')]: hovered && !sliding,
      }"
      :style="{ transform: `translateX(${indicatorLeft}px) translateZ(0)` }"
    />
    <ResizeObserver v-if="!noPreview" @resize="onPreviewResize">
      <div
        ref="preview"
        :class="{
          [ns.e('preview')]: true,
          [ns.em('preview', 'has-image')]: previewSrc,
          [ns.em('preview', 'active')]: hovered || sliding,
        }"
        :style="{ transform: `translateX(${previewLeft}px) translateZ(0)` }"
      >
        <slot name="preview">
          <div v-if="previewSrc" :class="ns.e('preview-image')">
            <img :src="previewSrc" />
          </div>
          <div :class="ns.e('preview-time')">
            {{ formatSeconds(hoveredTime) + segmentLabel }}
          </div>
        </slot>
      </div>
    </ResizeObserver>
  </div>
</template>
